'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: number;
  nameFr: string;
  nameDe: string;
  locationFr: string;
  locationDe: string;
  serviceFr: string;
  serviceDe: string;
  textFr: string;
  textDe: string;
  rating: number;
  image: string;
  alt: string;
}

interface TestimonialCarouselProps {
  className?: string;
}

const TestimonialCarousel = ({ className = '' }: TestimonialCarouselProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('fr');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
    const savedLanguage = localStorage.getItem('preferredLanguage') || 'fr';
    setCurrentLanguage(savedLanguage);
  }, []);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    nameFr: 'Marie Dubois',
    nameDe: 'Marie Dubois',
    locationFr: 'Le Locle',
    locationDe: 'Le Locle',
    serviceFr: 'Nettoyage résidentiel',
    serviceDe: 'Wohnungsreinigung',
    textFr: 'Service exceptionnel ! L\'équipe de Services Rouissi est professionnelle, ponctuelle et très minutieuse. Ma maison n\'a jamais été aussi propre. Je recommande vivement leurs services.',
    textDe: 'Außergewöhnlicher Service! Das Team von Services Rouissi ist professionell, pünktlich und sehr gründlich. Mein Haus war noch nie so sauber. Ich empfehle ihre Dienste wärmstens.',
    rating: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1104c87ff-1765607754886.png",
    alt: 'Smiling middle-aged woman with short brown hair wearing casual blue sweater in bright living room'
  },
  {
    id: 2,
    nameFr: 'Jean-Pierre Martin',
    nameDe: 'Jean-Pierre Martin',
    locationFr: 'La Chaux-de-Fonds',
    locationDe: 'La Chaux-de-Fonds',
    serviceFr: 'Services de conciergerie',
    serviceDe: 'Concierge-Dienstleistungen',
    textFr: 'Nous faisons confiance à Services Rouissi pour l\'entretien de notre immeuble depuis 3 ans. Leur fiabilité et leur qualité de travail sont irréprochables. Une équipe formidable !',
    textDe: 'Wir vertrauen Services Rouissi seit 3 Jahren für die Wartung unseres Gebäudes. Ihre Zuverlässigkeit und Arbeitsqualität sind einwandfrei. Ein großartiges Team!',
    rating: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ed465880-1763294125467.png",
    alt: 'Professional businessman in his fifties with gray hair wearing navy suit smiling confidently in modern office'
  },
  {
    id: 3,
    nameFr: 'Sophie Renaud',
    nameDe: 'Sophie Renaud',
    locationFr: 'Le Locle',
    locationDe: 'Le Locle',
    serviceFr: 'Nettoyage commercial',
    serviceDe: 'Gewerbliche Reinigung',
    textFr: 'Notre bureau est toujours impeccable grâce à Services Rouissi. Ils s\'adaptent parfaitement à nos horaires et sont très discrets. Un service de qualité suisse !',
    textDe: 'Unser Büro ist dank Services Rouissi immer makellos. Sie passen sich perfekt an unsere Zeitpläne an und sind sehr diskret. Ein Service in Schweizer Qualität!',
    rating: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16619e93b-1763299693932.png",
    alt: 'Young professional woman with long blonde hair in white blouse smiling warmly in bright office environment'
  }];


  const content = {
    fr: {
      title: 'Ce que disent nos clients',
      subtitle: 'Plus de 500 clients satisfaits dans la région du Locle',
      viewAll: 'Voir tous les témoignages'
    },
    de: {
      title: 'Was unsere Kunden sagen',
      subtitle: 'Über 500 zufriedene Kunden in der Region Le Locle',
      viewAll: 'Alle Testimonials anzeigen'
    }
  };

  const currentContent = isHydrated ? content[currentLanguage as keyof typeof content] : content.fr;

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className={`py-16 lg:py-24 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            {currentContent.title}
          </h2>
          <p className="text-lg text-muted-foreground">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Carousel */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-card border border-border rounded-2xl shadow-xl p-8 lg:p-12 relative">
            {/* Navigation Buttons */}
            <button
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-surface hover:bg-primary hover:text-primary-foreground rounded-full flex items-center justify-center transition-all duration-300 shadow-md z-10"
              aria-label="Previous testimonial">

              <Icon name="ChevronLeftIcon" size={24} variant="outline" />
            </button>

            <button
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-surface hover:bg-primary hover:text-primary-foreground rounded-full flex items-center justify-center transition-all duration-300 shadow-md z-10"
              aria-label="Next testimonial">

              <Icon name="ChevronRightIcon" size={24} variant="outline" />
            </button>

            {/* Testimonial Content */}
            <div className="flex flex-col items-center text-center">
              {/* Quote Icon */}
              <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mb-6">
                <Icon name="ChatBubbleLeftRightIcon" size={32} variant="solid" className="text-accent" />
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-6">
                {[...Array(currentTestimonial.rating)].map((_, i) =>
                <Icon
                  key={i}
                  name="StarIcon"
                  size={24}
                  variant="solid"
                  className="text-accent" />

                )}
              </div>

              {/* Testimonial Text */}
              <p className="text-lg lg:text-xl text-foreground mb-8 leading-relaxed max-w-2xl">
                "{isHydrated && currentLanguage === 'de' ? currentTestimonial.textDe : currentTestimonial.textFr}"
              </p>

              {/* Client Info */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-primary">
                  <AppImage
                    src={currentTestimonial.image}
                    alt={currentTestimonial.alt}
                    className="w-full h-full object-cover" />

                </div>
                <div className="text-left">
                  <div className="font-heading font-bold text-foreground">
                    {isHydrated && currentLanguage === 'de' ? currentTestimonial.nameDe : currentTestimonial.nameFr}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {isHydrated && currentLanguage === 'de' ? currentTestimonial.locationDe : currentTestimonial.locationFr}
                  </div>
                  <div className="text-xs text-primary font-medium">
                    {isHydrated && currentLanguage === 'de' ? currentTestimonial.serviceDe : currentTestimonial.serviceFr}
                  </div>
                </div>
              </div>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) =>
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'bg-primary w-8' : 'bg-border'}`
                }
                aria-label={`Go to testimonial ${index + 1}`} />

              )}
            </div>
          </div>
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <a
            href="#testimonials"
            className="inline-flex items-center gap-2 text-primary hover:text-secondary font-heading font-semibold transition-colors duration-300">

            {currentContent.viewAll}
            <Icon name="ArrowRightIcon" size={20} variant="outline" />
          </a>
        </div>
      </div>
    </section>);

};

export default TestimonialCarousel;